var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/observability.ts
import { AppEnvironment, isNodeTestEnv } from "@leagueplatform/app-environment";
import { leagueRoutesMap } from "@leagueplatform/league-routes";
import { Auth } from "@leagueplatform/auth";

// src/sentry/sentry.ts
import {
  BrowserClient,
  defaultIntegrations,
  defaultStackParser,
  Hub,
  makeFetchTransport
} from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import { reactRouterV5Instrumentation } from "@sentry/react";
import { history } from "@leagueplatform/routing";

// src/observability.utils.ts
import merge from "lodash.merge";
import { isDev } from "@leagueplatform/app-environment";
var ENABLE_DEV_FLAG_STRING = "ENABLE_DEV_OBSERVABILITY";
var DISABLED_TENANT_IDS = ["presenter"];
var isTenantDisabled = (tenantId) => tenantId ? DISABLED_TENANT_IDS.includes(tenantId) : false;
var combineErrorContexts = (contexts) => contexts.reduce(
  (previousContext, currentContext) => merge(previousContext, currentContext)
);
var isObservabilityEnabled = (tenantId) => (!isDev() || sessionStorage.getItem(ENABLE_DEV_FLAG_STRING) === "true") && !isTenantDisabled(tenantId);

// src/sentry/sentry.utils.ts
var EVENT_IGNORE_CRITERIA = [
  "<unknown>",
  "ChunkLoadError",
  /WebSocket closed with reason: \(1006\)/
];
var setSentryContext = (errorContext, scope) => {
  var _a, _b, _c;
  if (errorContext.tags)
    scope.setTags(errorContext.tags);
  if (errorContext.ownershipTeam || ((_a = errorContext.tags) == null ? void 0 : _a.moduleName))
    scope.setTag(
      "ownershipTeam",
      (_c = errorContext.ownershipTeam) != null ? _c : (_b = errorContext.tags) == null ? void 0 : _b.moduleName
    );
  if (errorContext.context) {
    Object.entries(errorContext.context).forEach(
      (contextData) => {
        scope.setContext(contextData[0], contextData[1]);
      }
    );
  }
  return scope;
};

// src/sentry/sentry.ts
var DEFAULT_SAMPLE_RATE = 1;
var leagueSDKSentryHub;
var isInitialized = (targetHub = leagueSDKSentryHub) => {
  if (targetHub)
    return true;
  if (isObservabilityEnabled()) {
    const targetHubType = targetHub === leagueSDKSentryHub ? "League SDK" : "APP";
    console.warn(
      `Observability - ${targetHubType} instance of Sentry has not been initialized.`
    );
  }
  return false;
};
var startCustomTransaction = (transactionContext) => {
  if (!isInitialized())
    return null;
  return leagueSDKSentryHub.startTransaction(transactionContext);
};
var getActiveTransaction = () => {
  var _a, _b;
  return (_b = (_a = Sentry.getCurrentHub()) == null ? void 0 : _a.getScope()) == null ? void 0 : _b.getTransaction();
};
var initHub = (metadata, config) => {
  const client = new BrowserClient({
    dsn: config.dsn,
    environment: config.environment,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    integrations: [
      ...defaultIntegrations.filter(
        (integration) => integration.name !== "Dedupe"
      ),
      new Sentry.Integrations.GlobalHandlers({
        onerror: false,
        onunhandledrejection: false
      })
    ],
    sampleRate: config.errorsSampleRate || DEFAULT_SAMPLE_RATE,
    tracesSampleRate: config.tracesSampleRate,
    ignoreErrors: EVENT_IGNORE_CRITERIA
  });
  leagueSDKSentryHub = new Hub(client);
  leagueSDKSentryHub.setTags(__spreadProps(__spreadValues({}, metadata), { originHub: "SDK" }));
};
var initSDK = (config) => {
  Sentry.init({
    dsn: config.dsn,
    integrations: [
      new Sentry.BrowserTracing(
        config.integrateWithLeagueRouting ? {
          /**
           * this `history` instance is imported from `@leagueplatform/routing`,
           * which means it will be the same instance used by the `<BrowserRouter>` component
           * exported by that module. Participating apps will therefore mount a Router
           * that sentry will share a `history` instance with.
           */
          routingInstrumentation: reactRouterV5Instrumentation(history)
        } : void 0
      )
    ],
    environment: config.environment,
    tracesSampleRate: config.tracesSampleRate,
    sampleRate: config.errorsSampleRate || DEFAULT_SAMPLE_RATE,
    ignoreErrors: EVENT_IGNORE_CRITERIA
  });
  Sentry.setTags({ originHub: "APP" });
};
var captureError = (error, options) => {
  var _a, _b, _c, _d, _e, _f;
  const capturedError = error;
  if ((_a = options == null ? void 0 : options.context) == null ? void 0 : _a.errorName)
    capturedError.name = (_b = options == null ? void 0 : options.context) == null ? void 0 : _b.errorName;
  if ((_d = (_c = options == null ? void 0 : options.context) == null ? void 0 : _c.tags) == null ? void 0 : _d.moduleName)
    capturedError.name = `${String((_f = (_e = options == null ? void 0 : options.context) == null ? void 0 : _e.tags) == null ? void 0 : _f.moduleName)} - ${capturedError.name}`;
  const targetHub = (options == null ? void 0 : options.targetHub) === "APP" /* APP */ ? Sentry.getCurrentHub() : leagueSDKSentryHub;
  if (isInitialized(targetHub))
    targetHub.run((currentHub) => {
      currentHub.withScope((scope) => {
        if (options == null ? void 0 : options.context)
          setSentryContext(options == null ? void 0 : options.context, scope);
        currentHub.captureException(capturedError);
      });
    });
};
var captureMessage = (message, options) => {
  const targetHub = (options == null ? void 0 : options.targetHub) === "APP" /* APP */ ? Sentry.getCurrentHub() : leagueSDKSentryHub;
  if (isInitialized(targetHub))
    targetHub.run((currentHub) => {
      currentHub.withScope((scope) => {
        var _a;
        if (options == null ? void 0 : options.context)
          setSentryContext(options == null ? void 0 : options.context, scope);
        currentHub.captureMessage(message, (_a = options == null ? void 0 : options.context) == null ? void 0 : _a.severityLevel);
      });
    });
};
var setUserId = (userId) => {
  if (isInitialized()) {
    leagueSDKSentryHub.setUser({ id: userId != null ? userId : "" });
    Sentry.getCurrentHub().setUser({ id: userId != null ? userId : "" });
  }
};
var sentry_default = {
  initHub,
  initSDK,
  captureError,
  captureMessage,
  startCustomTransaction,
  getActiveTransaction,
  setUserId
};

// src/observability.ts
var DEFAULT_LEAGUE_SDK_URL_PATH = "/sentry-config/league/web-sdk";
var isInitializing;
var eventsQueue = { errors: [], messages: [] };
var getTenantConfigUrl = (tenantApiUrl, tenantId) => `${tenantApiUrl}/sentry-config/${tenantId}/web`;
var memoizedGetModuleNameFromUrl = () => {
  let cache;
  return () => {
    if ((cache == null ? void 0 : cache.path) === window.location.pathname)
      return cache.moduleName;
    const moduleName = Object.entries(leagueRoutesMap).filter(([, value]) => window.location.pathname.includes(String(value))).reduce(
      (longestValue, [key, value]) => value.length > longestValue[1].length ? [key, value] : longestValue,
      ["", ""]
    )[0];
    cache = { moduleName, path: window.location.pathname };
    return moduleName;
  };
};
var getModuleNameFromUrl = memoizedGetModuleNameFromUrl();
var captureQueuedEvents = () => {
  eventsQueue.errors.forEach((queuedError) => {
    sentry_default.captureError(queuedError.error, {
      context: __spreadValues({}, queuedError.context),
      targetHub: queuedError.targetHub
    });
  });
  eventsQueue.errors = [];
  eventsQueue.messages.forEach((queuedMessage) => {
    sentry_default.captureMessage(queuedMessage.message, {
      context: __spreadValues({}, queuedMessage.context),
      targetHub: queuedMessage.targetHub
    });
  });
  eventsQueue.messages = [];
};
var startTransaction = (transactionContext) => sentry_default.startCustomTransaction(transactionContext);
var finishTransaction = (transaction) => {
  transaction == null ? void 0 : transaction.finish();
};
var startSpan = (transaction, spanContext) => transaction == null ? void 0 : transaction.startChild(spanContext);
var startSpanOnActiveTransaction = (spanContext) => {
  const activeTransaction = sentry_default.getActiveTransaction();
  return activeTransaction == null ? void 0 : activeTransaction.startChild(spanContext);
};
var finishSpan = (span) => {
  span.finish();
};
var initObservability = (_0) => __async(void 0, [_0], function* ({
  metadata,
  tenantApiUrl,
  integrateWithLeagueRouting
}) {
  isInitializing = true;
  if (isObservabilityEnabled(metadata.tenantId)) {
    let isAppSentryConfig = true;
    let config;
    let response;
    try {
      response = yield fetch(
        getTenantConfigUrl(tenantApiUrl, metadata.tenantId)
      );
      if (response.status !== 200) {
        isAppSentryConfig = false;
        response = yield fetch(`${tenantApiUrl}${DEFAULT_LEAGUE_SDK_URL_PATH}`);
      }
      config = yield response == null ? void 0 : response.json();
    } catch (error) {
      isInitializing = false;
      return;
    }
    if (!config.enabled) {
      isInitializing = false;
      return;
    }
    sentry_default.initHub(metadata, __spreadProps(__spreadValues({}, config), {
      environment: AppEnvironment.environment
    }));
    if (isAppSentryConfig)
      sentry_default.initSDK(__spreadProps(__spreadValues({}, config), {
        environment: AppEnvironment.environment,
        integrateWithLeagueRouting
      }));
    isInitializing = false;
    captureQueuedEvents();
  }
});
var captureSentryError = (error, targetHub, context) => {
  Auth.getUserId().then((userId) => {
    sentry_default.setUserId(userId);
    const moduleName = targetHub === "SDK" /* SDK */ ? getModuleNameFromUrl() : "";
    if (isInitializing)
      eventsQueue.errors.push({
        error,
        context: __spreadProps(__spreadValues({}, context), { tags: __spreadProps(__spreadValues({}, context == null ? void 0 : context.tags), { moduleName }) }),
        targetHub
      });
    sentry_default.captureError(error, {
      context: __spreadProps(__spreadValues({}, context), { tags: __spreadProps(__spreadValues({}, context == null ? void 0 : context.tags), { moduleName }) }),
      targetHub
    });
  });
};
var captureSentryMessage = (message, targetHub, context) => {
  Auth.getUserId().then((userId) => {
    sentry_default.setUserId(userId);
    const moduleName = targetHub === "SDK" /* SDK */ ? getModuleNameFromUrl() : void 0;
    if (isInitializing)
      eventsQueue.messages.push({
        message,
        context: __spreadProps(__spreadValues({}, context), { tags: __spreadProps(__spreadValues({}, context == null ? void 0 : context.tags), { moduleName }) }),
        targetHub
      });
    sentry_default.captureMessage(message, {
      context: __spreadProps(__spreadValues({}, context), { tags: __spreadProps(__spreadValues({}, context == null ? void 0 : context.tags), { moduleName }) }),
      targetHub
    });
  });
};
var captureError2 = (error, context) => {
  if (!isNodeTestEnv()) {
    captureSentryError(error, "SDK" /* SDK */, context);
  }
};
var captureMessage2 = (message, context) => {
  if (!isNodeTestEnv()) {
    captureSentryMessage(message, "SDK" /* SDK */, context);
  }
};
var captureAppError = (error, context) => {
  if (!isNodeTestEnv()) {
    captureSentryError(error, "APP" /* APP */, context);
  }
};
var captureAppMessage = (message, context) => {
  if (!isNodeTestEnv()) {
    captureSentryMessage(message, "APP" /* APP */, context);
  }
};

// src/components/error-boundary.component.tsx
import * as React from "react";
var ObservabilityErrorBoundary = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  componentDidCatch(error, errorInfo) {
    const err = new Error(error);
    err.stack = error.stack;
    const { errorContext, onError } = this.props;
    captureError2(
      err,
      combineErrorContexts([
        {
          errorName: `Error Boundary: UI Error - ${err.message}`,
          context: { "Error Boundary Error Info": errorInfo }
        },
        errorContext || {}
      ])
    );
    if (onError)
      onError(err);
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    const { children, fallback } = this.props;
    const { hasError } = this.state;
    if (hasError) {
      return fallback || null;
    }
    return children;
  }
};

// src/components/route-with-observability.tsx
import { BaseRoute } from "@leagueplatform/routing";
import { withSentryRouting } from "@sentry/react";
import { isNodeTestEnv as isNodeTestEnv2 } from "@leagueplatform/app-environment";
var RouteWithObservability = !isNodeTestEnv2() ? withSentryRouting(BaseRoute) : BaseRoute;
export {
  ObservabilityErrorBoundary,
  RouteWithObservability,
  captureAppError,
  captureAppMessage,
  captureError2 as captureError,
  captureMessage2 as captureMessage,
  combineErrorContexts,
  finishSpan,
  finishTransaction,
  initObservability,
  startSpan,
  startSpanOnActiveTransaction,
  startTransaction
};
//# sourceMappingURL=index.js.map